import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@/lib/ThemeContext";
import { X, Search, Settings } from "lucide-react";

const isMac = typeof navigator !== "undefined" && navigator.platform.includes("Mac");

const SHORTCUT_GROUPS = [
  {
    title: "Global", shortcuts: [
      { label: "Command Palette", keys: ["mod", "K"] },
      { label: "Show Shortcuts", keys: ["?"] },
      { label: "Open Settings", keys: ["mod", ","] },
    ],
  },
  {
    title: "Timer", shortcuts: [
      { label: "Start / Pause", keys: ["Space"] },
      { label: "Reset Timer", keys: ["R"] },
      { label: "Close Timer", keys: ["Esc"] },
    ],
  },
  {
    title: "Tasks", shortcuts: [
      { label: "New Task", keys: ["N"] },
      { label: "Focus Search", keys: ["/"] },
      { label: "Complete Task", keys: ["mod", "Enter"] },
    ],
  },
  {
    title: "Navigation", shortcuts: [
      { label: "Go to Dashboard", keys: ["G", "then", "D"] },
      { label: "Go to Tasks", keys: ["G", "then", "T"] },
      { label: "Go to Projects", keys: ["G", "then", "P"] },
      { label: "Go to Settings", keys: ["G", "then", "S"] },
    ],
  },
];

function formatKey(k) {
  if (k === "mod") return isMac ? "\u2318" : "Ctrl";
  if (k === "then") return null;
  return k;
}

function KeyCap({ k }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[24px] h-[22px] px-1.5 text-[11px] font-mono font-bold rounded"
      style={{
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.12)",
        color: "rgba(255,255,255,0.8)",
        boxShadow: "0 1px 0 rgba(255,255,255,0.05)",
      }}>
      {k}
    </kbd>
  );
}

export default function ShortcutOverlay() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { tokens: t } = useTheme();
  const navigate = useNavigate();
  const searchRef = useRef(null);

  const handleKeyDown = useCallback((e) => {
    // Don't trigger in inputs
    if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) return;
    if (e.key === "?" || (e.shiftKey && e.key === "/")) {
      e.preventDefault();
      setOpen(prev => !prev);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (open && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 100);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  if (!open) return null;

  const filteredGroups = SHORTCUT_GROUPS.map(group => ({
    ...group,
    shortcuts: group.shortcuts.filter(sc =>
      sc.label.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter(group => group.shortcuts.length > 0);

  return (
    <div className="fixed inset-0 z-[180] flex items-center justify-center" data-testid="shortcut-overlay"
      role="dialog" aria-label="Keyboard shortcuts">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative w-full max-w-lg mx-4 max-h-[80vh] rounded-xl shadow-2xl overflow-hidden flex flex-col mmntm-popup border"
        style={{ background: "var(--tm-bg-card)", borderColor: "var(--tm-accent)" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <h2 className="text-sm font-bold text-white">Keyboard Shortcuts</h2>
          <button onClick={() => setOpen(false)} className="p-1 rounded hover:opacity-70" aria-label="Close shortcuts"
            data-testid="shortcut-overlay-close">
            <X className="h-4 w-4 text-white/50" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-2" style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <Search className="h-3.5 w-3.5 text-white/30" />
            <input ref={searchRef} value={search} onChange={(e) => setSearch(e.target.value)}
              className="flex-1 text-sm bg-transparent outline-none text-white placeholder:text-white/30"
              placeholder="Search shortcuts..." data-testid="shortcut-search" />
          </div>
        </div>

        {/* Shortcut list */}
        <div className="overflow-y-auto px-5 py-3 space-y-4 flex-1">
          {filteredGroups.map(group => (
            <div key={group.title}>
              <h3 className="text-[10px] font-bold uppercase tracking-wider mb-2"
                style={{ color: "rgba(168, 85, 247, 0.7)" }}>{group.title}</h3>
              <div className="space-y-0.5">
                {group.shortcuts.map(sc => (
                  <div key={sc.label} className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-white/[0.03]"
                    data-testid={`shortcut-ref-${sc.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <span className="text-[13px] text-white/80">{sc.label}</span>
                    <span className="flex items-center gap-1">
                      {sc.keys.map((k, i) => {
                        const formatted = formatKey(k);
                        if (formatted === null) return <span key={`${sc.label}-then-${i}`} className="text-[9px] text-white/30 mx-0.5">then</span>;
                        return <KeyCap key={`${sc.label}-${k}`} k={formatted} />;
                      })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {filteredGroups.length === 0 && (
            <div className="text-center py-8 text-sm text-white/30">No matching shortcuts</div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-2.5 flex items-center justify-between" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <button onClick={() => { setOpen(false); navigate("/settings/shortcuts"); }}
            className="flex items-center gap-1.5 text-[11px] font-semibold hover:opacity-80"
            style={{ color: "rgba(168, 85, 247, 0.8)" }} data-testid="shortcut-customize-link">
            <Settings className="h-3 w-3" /> Customize shortcuts
          </button>
          <span className="text-[10px] text-white/30">Press <KeyCap k="Esc" /> to close</span>
        </div>
      </div>
    </div>
  );
}
